import { useMemo } from 'react'
import { ColumnTable } from 'arquero'
import { CellInfoGroup, CellPlotData, PinnedMorphotype } from 'redux/slices/types'
import { getColumn } from '../arrow/arqueroHelpers'

interface PlotRange {
  x1?: number
  x2?: number
  y1?: number
  y2?: number
}

interface ClusterCentroid {
  clusterValue: string
  x: number
  y: number
  cellCount: number
}

interface UseLeidenClusterCentroidsResult {
  centroidTrace: CellPlotData | null
  centroidShapes: Partial<Plotly.Shape>[]
  centroidAnnotations: Partial<Plotly.Annotations>[]
  clusterCentroids: ClusterCentroid[]
}

/**
 * Calculate centroids for each Leiden cluster and return shapes/annotations
 * with (+) buttons at centroid positions for creating morphotypes.
 * Also returns scatter trace with markers for click detection.
 *
 * We try to use buttons that are similar in style to the MUI buttons
 * in the panel.
 *
 * Plus we try to make the buttons a fixed size that's big enough to be
 * clickable in screen space (about 40px diameter)
 *
 * Memory-efficient: operates directly on DataFrame columns instead of creating object arrays
 *
 * @param dataFrame - DataFrame containing cell information
 * @param isLeidenSelectMode - Whether the user is in Leiden Select mode (shows buttons regardless of color-by setting)
 * @param hoveredClusterId - Optional cluster ID being hovered to show hover region
 * @param isPreFilter - Whether we're in prefilter mode (affects tooltip message)
 * @param morphotypesToRemove - List of morphotypes to remove (for hiding already-added clusters in prefilter mode)
 * @param prefilteredCellIdSet - Set of cell IDs that have been prefiltered out
 */
export const useLeidenClusterCentroidsFromDataFrame = (
  dataFrame: ColumnTable | null,
  clusterData: CellInfoGroup['data'] | undefined,
  isLeidenSelectMode: boolean,
  isMultiPlotMode = false,
  range?: PlotRange,
  plotPixelWidth = 600,
  hoveredClusterId?: string | null,
  isPreFilter = false,
  morphotypesToRemove?: PinnedMorphotype[],
  prefilteredCellIdSet?: Set<string>
): UseLeidenClusterCentroidsResult => {
  // Extract primitive values from range to stabilize useMemo dependencies
  const rangeX1 = range?.x1
  const rangeX2 = range?.x2

  const { centroidTrace, centroidShapes, centroidAnnotations, clusterCentroids } = useMemo(() => {
    // Only calculate centroids when in Leiden Select mode
    if (!isLeidenSelectMode || !dataFrame || !clusterData || clusterData.length === 0 || dataFrame.numRows() === 0) {
      return { centroidTrace: null, centroidShapes: [], centroidAnnotations: [], clusterCentroids: [] }
    }

    // Hide + buttons in multi-plot mode to reduce clutter
    if (isMultiPlotMode) {
      return { centroidTrace: null, centroidShapes: [], centroidAnnotations: [], clusterCentroids: [] }
    }

    // Get list of visible cluster values from clusterData (excluding hidden ones)
    const visibleClusterValues = new Set(
      clusterData.filter(item => !item.isHidden).map(item => String(item.value))
    )

    // Group cells by Leiden cluster - only for visible clusters
    const clusterMap = new Map<string, { sumX: number; sumY: number; count: number }>()

    // Extract columns from DataFrame (cached to prevent redundant .toArray() calls)
    const cellIdValues = getColumn<string>(dataFrame, 'CELL_ID')
    const leidenValues = getColumn<string | number>(dataFrame, 'leiden')
    const umapXValues = getColumn<number>(dataFrame, 'UMAP_0')
    const umapYValues = getColumn<number>(dataFrame, 'UMAP_1')

    for (let i = 0; i < leidenValues.length; i += 1) {
      const cellId = cellIdValues[i]
      const leidenValue = leidenValues[i]

      // Skip prefiltered cells (match scatter plot logic)
      const isPrefiltered = prefilteredCellIdSet && prefilteredCellIdSet.has(cellId)

      // Skip cells without leiden cluster assignment
      // Handle both string and number types, and check for empty/undefined
      if (
        !isPrefiltered &&
        leidenValue !== undefined &&
        leidenValue !== null &&
        leidenValue !== '' &&
        leidenValue !== 'undefined' &&
        leidenValue !== 'null'
      ) {
        // Convert to string for consistent handling
        const clusterValue = String(leidenValue).trim()

        // Skip empty cluster values or clusters not in the visible list
        if (clusterValue && visibleClusterValues.has(clusterValue)) {
          const x = Number(umapXValues[i])
          const y = Number(umapYValues[i])

          // Skip cells with invalid coordinates
          if (!Number.isNaN(x) && !Number.isNaN(y)) {
            const existing = clusterMap.get(clusterValue)
            if (existing) {
              existing.sumX += x
              existing.sumY += y
              existing.count += 1
            } else {
              clusterMap.set(clusterValue, { sumX: x, sumY: y, count: 1 })
            }
          }
        }
      }
    }

    // Calculate centroids for all visible clusters
    const allCentroids: ClusterCentroid[] = []

    clusterMap.forEach((data, clusterValue) => {
      const centroidXCoord = data.sumX / data.count
      const centroidYCoord = data.sumY / data.count

      allCentroids.push({
        clusterValue,
        x: centroidXCoord,
        y: centroidYCoord,
        cellCount: data.count,
      })
    })

    // Sort by cell count (descending) for consistent ordering
    const majorCentroids = allCentroids.sort((a, b) => b.cellCount - a.cellCount)

    // In prefilter mode, create a set of cluster values that are already in morphotypesToRemove
    const alreadyAddedClusters = isPreFilter && morphotypesToRemove
      ? new Set(
          morphotypesToRemove
            .filter((m) => m.leidenCluster !== undefined)
            .map((m) => m.leidenCluster)
        )
      : new Set()

    // Create arrays for trace data, shapes, and annotations
    const centroids: ClusterCentroid[] = []
    const centroidX: number[] = []
    const centroidY: number[] = []
    const hoverText: string[] = []
    const shapes: Partial<Plotly.Shape>[] = []
    const annotations: Partial<Plotly.Annotations>[] = []

    // Visual circle size: 20px outlined circle (matches ColorBy panel AddCircleOutlineIcon)
    // Hover region: 40px background circle
    // Clickable area: 40px invisible marker
    const visualCircleSizePx = 20
    const hoverRegionSizePx = 40
    const clickAreaSizePx = 40

    // Calculate circle radii in data coordinates based on current zoom level
    const plotWidth = (rangeX2 ?? 10) - (rangeX1 ?? 0)
    const visualCircleRadiusData = (visualCircleSizePx / 2 * plotWidth) / plotPixelWidth
    const hoverRegionRadiusData = (hoverRegionSizePx / 2 * plotWidth) / plotPixelWidth

    majorCentroids.forEach((centroid) => {
      // Skip this centroid if it's already been added to morphotypesToRemove in prefilter mode
      if (alreadyAddedClusters.has(centroid.clusterValue)) {
        return
      }

      centroids.push(centroid)
      centroidX.push(centroid.x)
      centroidY.push(centroid.y)
      const actionText = isPreFilter ? 'remove cells from this cluster' : 'create morphotype'
      hoverText.push(`Cluster ${centroid.clusterValue}<br>Click + to ${actionText}<br>${centroid.cellCount} cells`)

      // Create outlined circle shape (15px diameter, renders on top of hover region)
      // Shapes with layer:'above' render on top of images
      shapes.push({
        type: 'circle',
        xref: 'x',
        yref: 'y',
        x0: centroid.x - visualCircleRadiusData,
        y0: centroid.y - visualCircleRadiusData,
        x1: centroid.x + visualCircleRadiusData,
        y1: centroid.y + visualCircleRadiusData,
        fillcolor: 'rgb(255, 255, 255)', // opaque white background
        line: {
          color: '#1976d2', // MUI primary.main color to match ColorBy panel
          width: 2, // Doubled line weight
        },
        layer: 'above', // Render above images
      })

      // Create annotation with + text
      // Annotations render above everything
      // captureevents: false allows hover events to pass through to the trace below
      annotations.push({
        x: centroid.x,
        y: centroid.y,
        text: '+',
        showarrow: false,
        font: {
          size: 16,
          color: '#1976d2', // MUI primary.main color
          family: 'Arial Black, sans-serif', // Bold for thicker appearance
        },
        xref: 'x',
        yref: 'y',
        xanchor: 'center',
        yanchor: 'middle',
        captureevents: false,
      })

      // Only add hover region circle if this centroid is being hovered
      if (hoveredClusterId === centroid.clusterValue) {
        shapes.push({
          type: 'circle',
          xref: 'x',
          yref: 'y',
          x0: centroid.x - hoverRegionRadiusData,
          y0: centroid.y - hoverRegionRadiusData,
          x1: centroid.x + hoverRegionRadiusData,
          y1: centroid.y + hoverRegionRadiusData,
          fillcolor: 'rgba(25, 118, 210, 0.4)', // MUI primary.light with high opacity
          line: {
            color: 'rgba(0, 0, 0, 0)', // No border
            width: 0,
          },
          layer: 'above', // Render above images
        })
      }
    })



    // Create invisible scatter trace for 30px clickable area
    // This trace won't be visible but will capture click events
    const trace: CellPlotData = {
      x: centroidX,
      y: centroidY,
      originalX: centroidX,
      originalY: centroidY,
      cellIds: centroids.map((c) => `cluster_centroid_${c.clusterValue}`),
      plotDatum: centroids.map((c) => ({
        x: c.x,
        y: c.y,
        cellId: `cluster_centroid_${c.clusterValue}`,
      })),
      mode: 'markers',
      type: 'scatter',
      marker: {
        size: clickAreaSizePx,
        color: 'rgba(0, 0, 0, 0)', // Fully transparent
        symbol: 'circle',
      },
      hoverinfo: 'text',
      hovertext: hoverText,
      showlegend: false,
      name: 'leiden_cluster_centroids',
      xaxis: 'x',
      yaxis: 'y',
    }

    return {
      centroidTrace: trace,
      centroidShapes: shapes,
      centroidAnnotations: annotations,
      clusterCentroids: centroids,
    }
  }, [dataFrame, clusterData, isLeidenSelectMode, isMultiPlotMode, rangeX1, rangeX2, plotPixelWidth, hoveredClusterId, isPreFilter, morphotypesToRemove, prefilteredCellIdSet])

  return { centroidTrace, centroidShapes, centroidAnnotations, clusterCentroids }
}

export default useLeidenClusterCentroidsFromDataFrame
